/**
 * PESTLE API Service
 * Service for fetching PESTLE news analysis and scoring data
 */

import { API_BASE_URL as PESTLE_API_BASE } from './apiConfig';

/**
 * Get PESTLE statistics
 * @returns {Promise<Object>} PESTLE statistics data
 */
export const getPestleStats = async () => {
  try {
    const response = await fetch(`${PESTLE_API_BASE}/pestle-stats`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching PESTLE stats:', error);
    return {
      success: false,
      stats: null,
      error: error.message
    };
  }
};

/**
 * Get PESTLE scoring status
 * @returns {Promise<Object>} PESTLE scoring service status
 */
export const getPestleScoringStatus = async () => {
  try {
    const response = await fetch(`${PESTLE_API_BASE}/pestle-scoring-status`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching PESTLE scoring status:', error);
    return {
      success: false,
      status: null,
      error: error.message
    };
  }
};

/**
 * Get recent PESTLE analyzed articles
 * @param {number} limit - Maximum number of articles to return
 * @returns {Promise<Array>} Array of recent PESTLE analyzed articles
 */
/**
 * Landing page news: analyzed_articles filtered to critical minerals + India perspective.
 * @param {number} limit - Max articles (default 10)
 * @returns {Promise<Array>}
 */
/**
 * Top critical/impactful analyzed articles for corporate Command Centre alert banner.
 * @param {number} limit
 * @returns {Promise<{ alert: object|null, articles: Array }>}
 */
export const getCorporateCriticalAlert = async (limit = 5, days = 7) => {
  try {
    const response = await fetch(
      `${PESTLE_API_BASE}/api/analyzed-articles/corporate-critical-alert?limit=${limit}&days=${days}`,
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return {
      alert: data?.alert || null,
      articles: Array.isArray(data?.articles) ? data.articles : [],
    };
  } catch (error) {
    console.error('Error fetching corporate critical alert:', error);
    return { alert: null, articles: [] };
  }
};

export const getLandingIndiaCriticalMineralsNews = async (limit = 10, days = 30) => {
  try {
    const response = await fetch(
      `${PESTLE_API_BASE}/api/analyzed-articles/landing-news?limit=${limit}&days=${days}`,
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return Array.isArray(data?.articles) ? data.articles : [];
  } catch (error) {
    console.error('Error fetching landing India critical minerals news:', error);
    return [];
  }
};

export const getRecentPestleArticles = async (limit = 50) => {
  try {
    const response = await fetch(`${PESTLE_API_BASE}/api/analyzed-articles?limit=${limit}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    // Backend returns articles array directly, not wrapped in an object
    return Array.isArray(data) ? data : (data.articles || []);
  } catch (error) {
    console.error('Error fetching recent PESTLE articles:', error);
    return [];
  }
};

/**
 * Get PESTLE analyzed articles filtered by mineral
 * @param {string} mineral - Mineral name to filter by
 * @param {number} limit - Maximum number of articles to return
 * @returns {Promise<Array>} Array of PESTLE analyzed articles for the specified mineral
 */
export const getPestleArticlesByMineral = async (mineral, limit = 20) => {
  try {
    const response = await fetch(`${PESTLE_API_BASE}/api/analyzed-articles?mineral=${encodeURIComponent(mineral)}&limit=${limit}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return Array.isArray(data) ? data : (data.articles || []);
  } catch (error) {
    console.error('Error fetching PESTLE articles by mineral:', error);
    return [];
  }
};

/**
 * Get PESTLE news summary for dashboard
 * @returns {Promise<Object>} PESTLE news summary data
 */
export const getPestleNewsSummary = async () => {
  try {
    const [articles, stats] = await Promise.all([
      getRecentPestleArticles(1000), // Increased limit to get more accurate mineral counts
      getPestleStats()
    ]);
    
    // Analyze articles by PESTLE category
    const categoryStats = {};
    const riskLevelStats = {};
    const mineralStats = {};
    const countryStats = {};
    
    articles.forEach(article => {
      // Category stats
      const category = article.pestle_category || 'Unknown';
      categoryStats[category] = (categoryStats[category] || 0) + 1;
      
      // Risk level stats
      const riskLevel = article.impact_level || article.pestle_risk_level || 'Unknown';
      riskLevelStats[riskLevel] = (riskLevelStats[riskLevel] || 0) + 1;
      
      // Mineral stats
      if (article.minerals && Array.isArray(article.minerals)) {
        article.minerals.forEach(mineral => {
          mineralStats[mineral] = (mineralStats[mineral] || 0) + 1;
        });
      }
      
      // Country stats
      if (article.countries_involved && Array.isArray(article.countries_involved)) {
        article.countries_involved.forEach(country => {
          countryStats[country] = (countryStats[country] || 0) + 1;
        });
      }
    });
    
    // Get top minerals, but ensure specific treemap minerals are always included
    const treemapMinerals = ['Lithium', 'Cobalt', 'Nickel', 'Graphite', 'Rare Earth Elements'];
    const sortedMinerals = Object.entries(mineralStats)
      .sort(([,a], [,b]) => b - a);
    
    // Create a set of minerals we want to include
    const mineralsToInclude = new Set();
    
    // Add the top 5 minerals
    sortedMinerals.slice(0, 5).forEach(([mineral]) => mineralsToInclude.add(mineral));
    
    // Add specific treemap minerals (even if not in top 5)
    treemapMinerals.forEach(mineral => {
      if (mineralStats[mineral] > 0) {
        mineralsToInclude.add(mineral);
      }
    });
    
    const topMinerals = sortedMinerals
      .filter(([mineral]) => mineralsToInclude.has(mineral))
      .map(([mineral, count]) => ({ mineral, count }));
    
    return {
      totalArticles: articles.length,
      categoryBreakdown: categoryStats,
      riskLevelBreakdown: riskLevelStats,
      topMinerals: topMinerals,
      topCountries: Object.entries(countryStats)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([country, count]) => ({ country, count })),
      recentArticles: articles.slice(0, 10),
      pestleStats: stats.stats || null
    };
  } catch (error) {
    console.error('Error fetching PESTLE news summary:', error);
    return {
      totalArticles: 0,
      categoryBreakdown: {},
      riskLevelBreakdown: {},
      topMinerals: [],
      topCountries: [],
      recentArticles: [],
      pestleStats: null
    };
  }
};

/**
 * Get PESTLE color for UI display
 * @param {string} category - PESTLE category
 * @returns {string} CSS color class
 */
export const getPestleColor = (category) => {
  const colorMap = {
    'Political': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
    'political': 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
    'Economic': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    'economic': 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    'Social': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
    'social': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
    'Technological': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
    'technological': 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
    'Legal': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
    'legal': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
    'Environmental': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    'environmental': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
  };
  return colorMap[category] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
};

/**
 * Get risk level color for UI display
 * @param {string} riskLevel - Risk level
 * @returns {string} CSS color class
 */
export const getRiskLevelColor = (riskLevel) => {
  const colorMap = {
    'Critical': 'bg-red-600 text-white',
    'High': 'bg-red-500 text-white',
    'Medium': 'bg-yellow-500 text-white',
    'Low': 'bg-green-500 text-white',
    'critical': 'bg-red-600 text-white',
    'high': 'bg-red-500 text-white',
    'medium': 'bg-yellow-500 text-white',
    'low': 'bg-green-500 text-white'
  };
  return colorMap[riskLevel] || 'bg-gray-500 text-white';
};

/**
 * Format article date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatArticleDate = (dateString) => {
  if (!dateString) return 'Recent';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    return 'Recent';
  }
};

/**
 * Get risk level dot color for indicators
 * @param {string} riskLevel - Risk level string
 * @returns {string} CSS class for dot color
 */
export const getRiskLevelDotColor = (riskLevel) => {
  switch (riskLevel?.toLowerCase()) {
    case 'critical': return 'bg-red-600';
    case 'high': return 'bg-red-400';
    case 'medium': return 'bg-yellow-400';
    case 'low': return 'bg-green-400';
    default: return 'bg-gray-400';
  }
};

const pestleApi = {
  getPestleStats,
  getPestleScoringStatus,
  getRecentPestleArticles,
  getPestleArticlesByMineral,
  getPestleNewsSummary,
  getPestleColor,
  getRiskLevelColor,
  getRiskLevelDotColor,
  formatArticleDate,
};

export default pestleApi;
